import { useEffect, useMemo } from 'react';

import { useField } from 'libs/form/fields/useField';
import { useTranslation } from 'libs/i18n';
import { Table, TableSetRowHref } from 'libs/ui';
import { WithdrawalAccountFormType } from 'modules/client/common/forms/withdrawalAccountForm';
import { v4 as uuidv4 } from 'uuid';

import { useColumns } from './columns';
import { WithdrawalAccountItem, WithdrawalAccountOptionType } from './types';

interface Props {
  editable?: boolean;
  items: WithdrawalAccountFormType[];
  onCellClick?: (row: WithdrawalAccountItem) => void;
  onClickDelete?: (row: WithdrawalAccountItem) => void;
  setRowHref?: TableSetRowHref<WithdrawalAccountItem>;
}

export const WithdrawalAccountsTable = ({
  items,
  editable,
  onCellClick,
  onClickDelete,
  setRowHref,
}: Props) => {
  const { t } = useTranslation();

  const { helpers, meta } = useField('withdrawalAccounts');

  const shownOptions: Partial<Record<WithdrawalAccountOptionType, boolean>> = {
    delete: !!onClickDelete,
  };

  const rows: WithdrawalAccountItem[] = useMemo(
    () =>
      items.map((v, i) => ({
        number: i + 1,
        index: i,
        id: uuidv4(),
        ...v,
      })),
    [items]
  );

  const isError = rows.length === 0 && !!meta.isError;

  const onClickOption = (
    item: WithdrawalAccountItem,
    option: WithdrawalAccountOptionType
  ) => {
    switch (option) {
      case WithdrawalAccountOptionType.delete:
        onClickDelete?.(item);

        break;
      default:
        break;
    }
  };

  const columns = useColumns({
    editable,
    shownOptions,
    onClickOption,
  });

  useEffect(() => {
    if (!meta.isError || rows.length > 0) {
      helpers.setError(undefined);
    }
  }, [rows.length, helpers, meta.isError]);

  return (
    <Table
      hideFooter
      columnHeadersVariant="small"
      columns={columns}
      isError={isError}
      noRowsLabel={t('client.client.withdrawalAccounts.noRowsLabel')}
      rowHeight={40}
      rows={rows}
      setRowHref={setRowHref}
      onCellClick={onCellClick}
    />
  );
};
